import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useCurrentSalesOrder } from '@/providers/SalesOrderProvider';
import { useRelatedPurchaseOrders } from '@/hooks/api/1330_related_orders';
import { usePrevSalesOrders } from '@/hooks/api/1262_prev_sales_orders';
import { useNextSalesOrders } from '@/hooks/api/1263_next_sales_orders';
import { Link, useNavigate } from 'react-router-dom';
import { MotionDiv } from '@/components/element/MotionDiv';
import { SalesOrderLogic, SalesOrderOutlineLogic } from '@/hooks/api/models/logics';
import { SalesOrderOutline } from '@/hooks/api/models';
import { Fragment, useEffect, useState } from 'react';
import { useUpdateSalesOrderNotRenewed } from '@/hooks/api/1264_update_not_renewed';
import { toastApiError } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useSalesOrderInvalidate } from '@/hooks/api/1260_sales_order';

export const RenewedSalesOrders = () => {
  const { setShowLoading } = useLoading();
  const navigate = useNavigate();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { _so_ } = useCurrentSalesOrder();
  const { data: prevOrders } = usePrevSalesOrders({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });
  const { data: nextOrders } = useNextSalesOrders({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });
  const { mutate, isSuccess, isPending, error } = useUpdateSalesOrderNotRenewed();
  const { invalidate: invalidateSalesOrder } = useSalesOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });
  const [notRenewed, setNotRenewed] = useState(_so_!.not_renewed);

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (isSuccess) {
      invalidateSalesOrder();
    }
  }, [isSuccess]);

  const onNotRenewedChecked = () => {
    setNotRenewed(!_so_!.not_renewed);
    mutate({ identifier: _w_!.identifier, team_id: _t_!.id, id: _so_!.id, not_renewed: !_so_!.not_renewed });
  };

  const onNewRenewedProjectClick = () => {
    navigate(`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/new`, { state: { copyFrom: _so_!, renewed: true } });
  };

  return (
    <>
      <MotionDiv className="card mb-4">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h6 className="mb-0">継続プロジェクト（売上）一覧</h6>
          <div className="d-flex align-items-center">
            {!_so_!.not_renewed && (
              <button onClick={onNewRenewedProjectClick} className="btn bg-gradient-primary btn-icon flex-shrink-0">
                <i className="fas fa-plus"></i>新規継続プロジェクト作成
              </button>
            )}
            <Link
              to={`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${_so_!.id}/renewed_sales_orders`}
              className="btn btn-outline-success btn-icon-only ms-1"
            >
              <i className="fas fa-edit"></i>
            </Link>
          </div>
        </div>
        <div className="card-body pt-0">
          {nextOrders == null ||
            (nextOrders.length === 0 && (
              <div className="d-flex align-items-center justify-content-between">
                <div className="text-xs font-weight-bold pe-2">以降の継続契約を行わない</div>
                <div className="px-0">
                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={notRenewed}
                      // defaultChecked={_so_!.not_renewed}
                      onChange={onNotRenewedChecked}
                    />
                  </div>
                </div>
              </div>
            ))}
          {nextOrders && nextOrders.length > 0 && (
            <>
              <hr className="horizontal dark" />
              <div className="font-weight-bold text-xs text-dark mb-3">以降のプロジェクト</div>
              {nextOrders.map((d, i) => {
                let dateLabel = SalesOrderOutlineLogic.toDateLabel(d);
                if (dateLabel.length > 0) {
                  dateLabel = `${dateLabel}, `;
                }

                return (
                  <Fragment key={`next-${d.id}`}>
                    <RenewedOrderRow d={d} />
                    {i != nextOrders.length - 1 && <hr className="horizontal dark" />}
                  </Fragment>
                );
              })}
            </>
          )}
          {prevOrders && prevOrders.length > 0 && (
            <>
              <hr className="horizontal dark" />
              <div className="font-weight-bold text-xs text-dark mb-3">以前のプロジェクト</div>
              {prevOrders.map((d, i) => {
                let dateLabel = SalesOrderOutlineLogic.toDateLabel(d);
                if (dateLabel.length > 0) {
                  dateLabel = `${dateLabel}, `;
                }

                return (
                  <Fragment key={`prev-${d.id}`}>
                    <RenewedOrderRow d={d} />
                    {i != prevOrders.length - 1 && <hr className="horizontal dark" />}
                  </Fragment>
                );
              })}
            </>
          )}
        </div>
      </MotionDiv>
    </>
  );
};

const RenewedOrderRow = ({ d }: { d: SalesOrderOutline }) => {
  const { _w_, _t_ } = useCurrentTeamMember();
  const { _so_ } = useCurrentSalesOrder();
  let dateLabel = SalesOrderOutlineLogic.toDateLabel(d);
  if (dateLabel.length > 0) {
    dateLabel = `${dateLabel}, `;
  }

  return (
    <>
      <Link
        to={`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${d.id}`}
        className="d-flex align-items-center justify-content-between mb-3"
      >
        <div className="d-flex align-items-center">
          <div className="icon icon-sm bg-dark d-flex border-radius-md align-items-center justify-content-center flex-shrink-0">
            <i className="fas fa-project-diagram text-white" aria-hidden="true"></i>
          </div>
          <div className="ms-2">
            <div className="font-weight-bold px-0 text-sm text-dark mb-1">{d.name}</div>
            <div className="text-muted font-weight-bold text-xs">
              {dateLabel}¥{SalesOrderLogic.totalAmount(d).toLocaleString()}
            </div>
          </div>
        </div>
        <i className="fas fa-angle-right text-muted opacity-3 flex-shrink-0 ms-2" aria-hidden="true"></i>
      </Link>
    </>
  );
};
